import { latLngToVector3, ThreeJSOverlayView } from "@googlemaps/three";
import { CatmullRomCurve3, ExtrudeGeometry, Scene, Vector3, Shape, Mesh, MeshLambertMaterial, AmbientLight, DirectionalLight } from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { APIResults } from "./sharedinterfaces";

const elevationService = new google.maps.ElevationService();

export class FlightViewer {
	public onPathFrame: (pathFrame: APIResults.FlightPathFrame) => void;
	public speed = 0;

	// Stays the same across operations
	private map: google.maps.Map;
	private scene: Scene;
	private flightPathLine: Line2;
	private assetModel: Mesh;
	private flightLineMaterial = new LineMaterial({ color: 0x009900, linewidth: 2, vertexColors: false });

	// Different per operation
	private basePath: APIResults.FlightPathFrame[];
	private elevatedPath: LatLngAltitudeLiteral[];
	private flightCurveWorldspace: CatmullRomCurve3;
	private flightCurveLatLng: CatmullRomCurve3;
	private orientationCurve: CatmullRomCurve3;

	private animationProgress = 0;
	private lastClock: number;
	private destroyed = false;

	private flightDurationS: number;
	private lastPathIndex: number;

	constructor(element: HTMLElement) {
		this.InitializeMap(element);
		this.AddMapControls();
		this.InitializeScene();
		this.AddAssetModel();
		this.StartAnimation();
		console.log("FlightViewer initialized");
	}

	public Destroy() {
		this.destroyed = true;
	}

	public async LoadPath(path: APIResults.FlightPathFrame[]) {
		this.speed = 0;
		if (this.onPathFrame) this.onPathFrame(undefined);
		if (!path || !path.length) throw new Error("No flight path data found");
		console.log("Loading path with " + path.length + " coordinate points");
		this.basePath = path;
		this.animationProgress = 0;
		this.lastClock = undefined;
		const startTime = this.basePath[0].ts;
		const endTime = this.basePath[this.basePath.length - 1].ts;
		this.flightDurationS = endTime - startTime;

		if (this.scene && this.flightPathLine) {
			this.scene.remove(this.flightPathLine);
			this.flightPathLine = undefined;
		}

		await this.CreateElevatedPath();
		this.CreateCurves();
		this.AddFlightPathLine();

		this.flightCurveWorldspace.getPoint(0, this.assetModel.position);
		this.speed = 6;
		this.SetProgress(0);
	}

	private StartAnimation() {
		if (this.destroyed) return;
		requestAnimationFrame(() => this.AnimationLoop());
	}

	public SetSpeed(speed: number) {
		this.speed = speed;
	}

	public SetProgress(progress: number) {
		this.animationProgress = progress;
		this.UpdateAnimation();
	}

	private async CreateElevatedPath() {
		const flatPath = ReduceArray(this.basePath.map(p => ({ lat: p.latitude, lng: p.longitude })), 100, true);
		const elevationResult = await elevationService.getElevationAlongPath({ path: flatPath, samples: 10 });
		// If all the points are the same, google spits out a bunch at 0, 0. We need to throw those away.
		const elevations = elevationResult.results.filter(e => e.location.lat() !== 0 || e.location.lng() !== 0);
		const elevationVectors = elevations.map(e => new Vector3(0, e.elevation, 0));
		// We had google give us 10 elevation samples for the whole path, so we'll need to interpolate between them
		const elevationCurve = new CatmullRomCurve3(elevationVectors, false, "catmullrom", 0.5);
		elevationCurve.updateArcLengths();

		// Create an copy of the path, with altitude converted to meters and adjusted to "above ground" by the elevation curve
		const elevatedPath: LatLngAltitudeLiteral[] = [];
		for (let i = 0; i < this.basePath.length; i++) {
			const elevationCurveSlice = elevationCurve.getPoint(i / (this.basePath.length - 1));
			elevatedPath.push({ lat: this.basePath[i].latitude, lng: this.basePath[i].longitude, altitude: AltitudeToTruncatedMeters(this.basePath[i].altitude, elevationCurveSlice.y) });
		}
		this.elevatedPath = elevatedPath;
	}

	private InitializeMap(element: HTMLElement) {
		this.map = new google.maps.Map(element, {
			mapId: "48a20f6cd4f99ee6",
			keyboardShortcuts: false,
			disableDefaultUI: true,
			fullscreenControl: true,
			backgroundColor: "transparent",
			gestureHandling: "greedy",
			tilt: 60.0,
			zoom: 18,
			mapTypeId: google.maps.MapTypeId.SATELLITE,
		});
	}
	private AddMapControls() {
		const rewind = document.createElement("div");
		rewind.className = "map-control rewind";
		rewind.innerHTML = "<div></div>";
		this.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(rewind);
		rewind.addEventListener("click", () => { this.SetSpeed(-6); });

		const pause = document.createElement("div");
		pause.className = "map-control pause";
		pause.innerHTML = "<div></div>";
		this.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(pause);
		pause.addEventListener("click", () => { this.SetSpeed(0); });

		const play = document.createElement("div");
		play.className = "map-control play";
		play.innerHTML = "<div></div>";
		this.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(play);
		play.addEventListener("click", () => { this.SetSpeed(6); });

		const play2 = document.createElement("div");
		play2.className = "map-control play2";
		play2.innerHTML = "<div></div>";
		this.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(play2);
		play2.addEventListener("click", () => { this.SetSpeed(12); });

		const play3 = document.createElement("div");
		play3.className = "map-control play3";
		play3.innerHTML = "<div></div>";
		this.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(play3);
		play3.addEventListener("click", () => { this.SetSpeed(24); });
	}

	private InitializeScene() {
		this.scene = new Scene();
		// const overlay = new ThreeJSOverlayView({ anchor: { lat: this.elevatedPath[0].lat, lng: this.elevatedPath[0].lng, altitude: 0 } , map: this.map, scene: this.scene });
		/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
		const overlay = new ThreeJSOverlayView({ map: this.map, scene: this.scene });
		const ambient = new AmbientLight(0xffffff, 0.5);
		this.scene.add(ambient);
		const directional = new DirectionalLight(0xffffff, 0.5);
		directional.position.set(0, 1, 0.1);
		directional.target.position.set(0, 0, 0);
		this.scene.add(directional);
		this.scene.add(directional.target);
	}

	private CreateCurves() {
		// Create a smooth curve from the flight path
		const flightVectors = this.elevatedPath.map(point => LatLngAltToVector3(point));
		// Note - centripetal gives fewer path hangnails but is not consistent between worldspace / latlng space (camera diverges)
		// Might be able to change back if we ever get a Vector3ToLatLngAlt function
		this.flightCurveWorldspace = new CatmullRomCurve3(flightVectors, false, "catmullrom", 0.5);
		this.flightCurveWorldspace.updateArcLengths();

		// Create a smooth curve from the actual lat/lng/alt we can use for the camera
		this.flightCurveLatLng = new CatmullRomCurve3(this.elevatedPath.map(point => new Vector3(point.lat, point.lng, point.altitude)), false, "catmullrom", 0.5);
		this.flightCurveWorldspace.updateArcLengths();

		// Create a curve for asset orientation
		// This should really be done with quaternions and slerp
		const orientationVectors = this.basePath.map(point => new Vector3(point.pitch, point.heading, point.roll));
		UnrollHeading(orientationVectors);
		this.orientationCurve = new CatmullRomCurve3(orientationVectors, false, "catmullrom", 0.5);
		this.orientationCurve.updateArcLengths();
	}

	private AddFlightPathLine() {
		const numPoints = 10 * this.flightCurveWorldspace.points.length;
		const curvePoints = this.flightCurveWorldspace.getSpacedPoints(numPoints);

		const floats = new Float32Array(curvePoints.length * 3);
		const geometry = new LineGeometry();
		for (let i = 0; i < curvePoints.length; i++) {
			curvePoints[i].toArray(floats, i * 3);
		}
		geometry.setPositions(floats);

		this.flightPathLine = new Line2(geometry, this.flightLineMaterial);
		this.flightPathLine.computeLineDistances();
		this.scene.add(this.flightPathLine);
	}

	private AddAssetModel() {
		const shape = new Shape();
		shape.moveTo(0, 2);
		shape.lineTo(2, -2);
		shape.lineTo(0, -1);
		shape.lineTo(-2, -2);
		shape.lineTo(0, 2);

		const geometry = new ExtrudeGeometry(shape, {
			steps: 2,
			depth: 1,
			bevelEnabled: false,
		});
		geometry.rotateX(-Math.PI / 2);
		geometry.scale(5, 5, 5);

		const mesh = new Mesh(geometry, new MeshLambertMaterial({ color: 0xcccccc }));
		this.assetModel = mesh;
		this.scene.add(this.assetModel);
	}

	private AnimationLoop() {
		// Stop updating if we are no longer active
		if (this.destroyed) return;

		if (this.speed !== 0) {
			this.UpdateAnimation();
		} else {
			this.lastClock = performance.now();
		}

		// Set the fat line resolution in case we resized
		this.flightLineMaterial.resolution?.set(this.map.getDiv().clientWidth, this.map.getDiv().clientHeight);

		requestAnimationFrame(() => this.AnimationLoop());
	}

	// Update animation progress, and move assetModel and camera
	UpdateAnimation() {
		if (!this.flightCurveWorldspace || !this.assetModel) return;
		// Figure out how long it's been since the last update, and advance the animation
		if (this.lastClock) {
			const delta = performance.now() - this.lastClock;
			this.animationProgress += this.speed * delta / (this.flightDurationS * 1000);
			if (this.animationProgress > 1) this.animationProgress = 1;
			if (this.animationProgress < 0) this.animationProgress = 0;
		}
		this.lastClock = performance.now();

		// Set model position from curve
		this.flightCurveWorldspace.getPoint(this.animationProgress, this.assetModel.position);

		const pathIndex = Math.min(Math.floor(this.animationProgress * this.basePath.length), this.basePath.length - 1);
		if (pathIndex !== this.lastPathIndex) {
			this.lastPathIndex = pathIndex;

			if (this.onPathFrame) this.onPathFrame(this.basePath[pathIndex]);
		}

		const orientation = this.orientationCurve.getPoint(this.animationProgress);
		this.assetModel.rotation.set(orientation.x * Math.PI / 180, -orientation.y * Math.PI / 180, -orientation.z * Math.PI / 180, "YXZ");

		// Update the camera
		const vectorLatLng = this.flightCurveLatLng.getPoint(this.animationProgress);
		const curvePosition = { lat: vectorLatLng.x, lng: vectorLatLng.y, altitude: vectorLatLng.z };
		this.map.moveCamera({
			center: curvePosition,
			// Zoom out as we increase altitude so the asset is still visible
			zoom: GetZoomLevel(curvePosition.altitude),
			heading: orientation.y,
			tilt: 60.0,
		});
		// // Scale up the asset with altitude, so as we zoom out we can still see it
		this.assetModel.scale.setScalar(1 + (curvePosition.altitude / 300));
	}
}

// Zoom as a function of altitude.
// Log function, except linear for the first 500m
function GetZoomLevel(altitude: number) {
	if (altitude < 500) return Math.min(18, 18 - altitude / 250);
	return 24.9 - 1.43 * Math.log(altitude);
}

function FeetToMeters(feet: number): number {
	return feet * 0.3048;
}

function AltitudeToTruncatedMeters(feet: number, baseElevationM: number): number {
	return Math.max(FeetToMeters(feet) - baseElevationM, 0);
}

function ReduceArray(array: any[], newLength: number, addLast = false) {
	if (newLength >= array.length) {
		return array.slice();
	}
	const factor = array.length / (array.length - newLength);
	const reduced = array.filter((e, i) => Math.floor(i % factor));
	// Add last element if requested
	if (addLast) {
		reduced.push(array[array.length - 1]);
	}
	return reduced;
}

// Smoothly transition over 360 degrees.
// Allows for crossing the 0 mark without a full spin
// This means if the asset starts pointing east and circles to the right twice, the heading will now be 810
function UnrollHeading(vectors: Vector3[]) {
	vectors[0].y = ((vectors[0].y % 360) + 360) % 360;
	let lastHeading = vectors[0].y;
	for (let i = 0; i < vectors.length; i++) {
		// Get the heading difference between this point and the previous one
		let delta = (((vectors[i].y - lastHeading) % 360) + 360) % 360;
		// If the difference is greater than 180 degrees, make it a left turn instead
		if (delta > 180) {
			delta = delta - 360;
		}
		// Update to the new heading. This may be greater than 360 or less than 0.
		vectors[i].y = lastHeading + delta;
		lastHeading = vectors[i].y;
	}
}

function LatLngAltToVector3(point: LatLngAltitudeLiteral): Vector3 {
	const vec = latLngToVector3(point);
	vec.y = point.altitude;
	return vec;
}

interface LatLngAltitudeLiteral extends google.maps.LatLngLiteral {
	altitude: number;
}